import { ValidationError } from '~/errors';
import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { validateId } from '~/repositories/utils';
import { ChatTimelineItemKind } from '~/swagger/portal/Api';
import { maintenanceServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { UploadFileOptions } from '~/repositories/uploadSessionRepository';
import type {
  ChatCreateResponseDto,
  ChatMessageDto,
  ChatMessagesDto,
  ChatNoteDto,
  ChatTypes,
  CreatePhoneChatDto,
  EntityType,
  HydratedMagicChatDto,
  MagicChatTextResponseDto,
  PostChatMessageDto,
  UnreadMessagePaginationDto,
  UpdateChatDto,
} from '~/swagger/portal/Api';
import type { ChatMessagesPage } from '~/swagger/portal/chatNotes';

const CHATS_URL = '/api/chats';

export function validateChat(chat: HydratedMagicChatDto | ChatMessageDto): void {
  if (!chat) throw new ValidationError('Chat object must be provided');
}

export interface ChatCreationResponse {
  chatId: string;
}

export function parseChatMessagesPage(json: ChatMessagesDto | ChatMessageDto[]): ChatMessagesPage {
  if (Array.isArray(json)) {
    return { messages: json, notes: [], propertyManagers: [], totalCount: json.length };
  }

  const items = json.items ?? [];
  const messages = items.length
    ? items.filter((item) => item.kind === ChatTimelineItemKind.Message && item.message).map((item) => item.message!)
    : (json.messages ?? []);
  const notes = items.filter((item) => item.kind !== ChatTimelineItemKind.Message && item.note).map((item) => item.note!);

  return {
    messages,
    notes,
    propertyManagers: json.propertyManagers ?? [],
    totalCount: json.totalCount ?? messages.length,
  };
}

export type ChatFilter = {
  closed?: boolean;
  humanEngagementRequested?: boolean;
  assignedPropertyManagerId?: string;
};

export type SendChatData = {
  message: string;
  sendText: boolean;
  sendEmail: boolean;
  uploadSessionIds?: string[];
};

export type UnreadMessageFilter = {
  page?: number;
  pageSize?: number;
};

export type ChatSearchFilter = {
  page?: number;
  pageSize?: number;
  search?: string;
  fileName?: string;
};

export type ChatsResponse =
  | {
      chats: HydratedMagicChatDto[];
    }
  | HydratedMagicChatDto[];

export class ChatRepository extends BaseRestRepository {
  public async getChats(filter?: ChatFilter): Promise<HydratedMagicChatDto[]> {
    const url = urlWithQuery(CHATS_URL, filter);
    const response = await this.fetchWithAuth(url);
    const jsonResponse: ChatsResponse = await this.getJsonResponse(response);

    return Array.isArray(jsonResponse) ? jsonResponse : jsonResponse.chats;
  }

  public async getChat(chatId: string): Promise<HydratedMagicChatDto> {
    const url = `${CHATS_URL}/${chatId}`;
    const response = await this.fetchWithAuth(url);

    return this.getJsonResponse(response);
  }

  public async getChatMessages(
    chatId: string,
    before?: string,
    after?: string,
    take = 20,
    search?: string,
    fileName?: string
  ): Promise<ChatMessagesPage> {
    validateId(chatId);

    const params = new URLSearchParams();
    if (before) {
      params.append('before', before);
    }
    if (after) {
      params.append('after', after);
    }
    params.append('take', take.toString());

    if (search) {
      params.append('search', search);
    }

    if (fileName) {
      params.append('fileName', fileName);
    }

    const url = `${CHATS_URL}/${chatId}/messages?${params.toString()}`;
    const response = await this.fetchWithAuth(url);
    const json: ChatMessagesDto | ChatMessageDto[] = await this.getJsonResponse(response);
    return parseChatMessagesPage(json);
  }

  public async createTextMessageChat(request: CreatePhoneChatDto): Promise<ChatCreateResponseDto> {
    const url = `${CHATS_URL}/phone`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return this.getJsonResponse(response);
  }

  public async sendChatMessage(chatId: string, chatData: SendChatData) {
    validateId(chatId, 'Chat');

    const url = `${CHATS_URL}/${chatId}/messages`;

    const payload: PostChatMessageDto = {
      message: chatData.message || '',
      sendText: chatData.sendText,
      sendEmail: chatData.sendEmail,
      uploadSessionIds: chatData.uploadSessionIds,
      aiEnhanced: false,
    };

    return await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  public async uploadChatMessageFiles(chatId: string, files: File[], opts?: UploadFileOptions): Promise<string[]> {
    validateId(chatId, 'Chat');
    if (files.length === 0) return [];

    return uploadSessionRepository.uploadFiles(`${CHATS_URL}/${chatId}/messages/upload-sessions`, files, opts);
  }

  public async createChatWindow(entryType: `${EntityType}` | `${ChatTypes}`, entryId: string): Promise<ChatCreateResponseDto> {
    let url;

    switch (entryType) {
      case 'lease':
        url = `/api/leases/${entryId}/chat`;
        break;
      case 'tenant':
        url = `/api/tenants/${entryId}/chat`;
        break;
      case 'vendor':
        url = `/api/vendors/${entryId}/chat`;
        break;
      case 'owner':
        url = `/api/owners/${entryId}/chat`;
        break;
      case 'rentalApplication':
        url = `/api/rental-applications/${entryId}/chat`;
        break;
      case 'maintenanceRequest':
        url = `${maintenanceServiceUrl()}/company-portal/maintenance-requests/${entryId}/chat`;
        break;
      case 'workOrder':
        url = `${maintenanceServiceUrl()}/company-portal/work-orders/${entryId}/chat`;
        break;
      case 'workOrderGroup':
        url = `${maintenanceServiceUrl()}/company-portal/work-orders/${entryId}/chat/group`;
        break;
      default:
        throw new Error('Invalid chat type');
    }

    const response = await this.fetchWithAuth(url, {
      method: 'POST',
    });

    if (response.ok) {
      const responseBody = await response.json();

      return responseBody;
    } else {
      throw new Error(`Failed to create chat. Server responded with status: ${response.status}`);
    }
  }

  public async sendChatMagicIt(chatId: string, chatData: string): Promise<MagicChatTextResponseDto | undefined> {
    validateId(chatId, 'Chat');

    const url = `${CHATS_URL}/${chatId}/magic`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ MyText: chatData }),
    });

    return this.getJsonResponse(response);
  }

  public async updateChat(chatId: string, updateChat: UpdateChatDto): Promise<HydratedMagicChatDto | undefined> {
    validateId(chatId, 'Chat');

    const url = `${CHATS_URL}/${chatId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(updateChat),
    });
    return this.getJsonResponse(response);
  }

  public async markMessagesAsRead(chatId: string): Promise<void> {
    validateId(chatId, 'Chat');

    const url = `${CHATS_URL}/${chatId}/messages/read/all`;
    await this.fetchWithAuth(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
      false
    );
  }

  public async paraphraseMessage(chatId: string, messageId: string): Promise<MagicChatTextResponseDto> {
    validateId(chatId, 'Chat');
    validateId(messageId, 'Message');

    const url = `${CHATS_URL}/${chatId}/messages/${messageId}/magic`;
    const response = await this.fetchWithAuth(url);

    return this.getJsonResponse(response);
  }

  public async archiveChat(chatId: string): Promise<HydratedMagicChatDto> {
    validateId(chatId, 'Chat');

    const url = `${CHATS_URL}/${chatId}/archive`;
    const response = await this.fetchWithAuth(url, {
      method: 'DELETE',
    });

    return this.getJsonResponse(response);
  }

  public async getUnreadMessage(filter: UnreadMessageFilter): Promise<UnreadMessagePaginationDto> {
    const baseUrl = `${CHATS_URL}/unread-messages`;
    const url = urlWithQuery(baseUrl, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async unarchiveChat(chatId: string): Promise<HydratedMagicChatDto> {
    validateId(chatId, 'Chat');
    const url = `${CHATS_URL}/${chatId}/unarchive`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
    });

    return this.getJsonResponse(response);
  }

  public async searchChat(filter: ChatSearchFilter): Promise<UnreadMessagePaginationDto> {
    const baseUrl = `${CHATS_URL}/search`;
    const url = urlWithQuery(baseUrl, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createChatNote(chatId: string, body: string, metadata?: Record<string, string>): Promise<ChatNoteDto> {
    validateId(chatId, 'Chat');

    const url = `${CHATS_URL}/${chatId}/notes`;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({ body, metadata }),
    });

    return this.getJsonResponse(response);
  }

  public async updateChatNote(chatId: string, noteId: string, body: string): Promise<ChatNoteDto> {
    validateId(chatId, 'Chat');
    validateId(noteId, 'Note');

    const url = `${CHATS_URL}/${chatId}/notes/${noteId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ body }),
    });

    return this.getJsonResponse(response);
  }

  public async deleteChatNote(chatId: string, noteId: string): Promise<void> {
    validateId(chatId, 'Chat');
    validateId(noteId, 'Note');

    const url = `${CHATS_URL}/${chatId}/notes/${noteId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' }, false);
  }

  public async setChatAssignee(chatId: string, propertyManagerId: string | null): Promise<HydratedMagicChatDto> {
    validateId(chatId, 'Chat');

    const url = `${CHATS_URL}/${chatId}/assignee`;
    const response = await this.fetchWithAuth(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ propertyManagerId }),
    });

    return this.getJsonResponse(response);
  }

  public async setHumanEngagement(chatId: string, requested: boolean): Promise<void> {
    validateId(chatId, 'Chat');

    const url = `${CHATS_URL}/${chatId}/human-engagement`;
    await this.fetchWithAuth(
      url,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requested }),
      },
      false
    );
  }
}
